import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { readPageParam, writePageParam } from './listPageParam';
import { isSiteAssigned } from '../../utils/siteAccess';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth';
import { useWebSocketStore } from '../../stores/websocket';
import { useGroupStore } from '../../stores/group';
import { useToastStore } from '../../stores/toast';
import { usePermissions } from '../../hooks/usePermissions';
import { useSites } from '../../hooks/useSites';
import { useReports } from '../../hooks/useReports';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import { useEnabledComposerTemplates } from '../../hooks/useEnabledComposerTemplates';
import {
  LEGACY_TYPE_PARAMS,
  TEMPLATE_KEY_PARAM,
  resolveEntryTemplateKey,
  resolveTemplateFilterOptions,
} from './documentTemplateFilter';
import {
  ReportStatus,
  getReportTypeLabel,
  getReportTypeBadgeTone,
  getDocumentDisplayTitle,
  getTBMDuration,
  getDocumentStats,
  DocumentStats,
  deleteReport,
  requestReportDelete,
  cancelReportDeleteRequest,
  downloadPdf,
  getPdfStatus,
  PdfStatus,
} from '../../lib/reports';
import { getTBMTagStyle } from '../../lib/tbm';
import { extractApiError } from '../../lib/api';
import { ServerEvents, ReportDeletedPayload, ReportCreatedNotifyPayload, ReportDeletedNotifyPayload, TbmEventPayload, ReportEventPayload, ReportDeleteRequestResolvedPayload } from '../../types/websocket';
import { formatDateToLocal, getEvidenceDisplayName, ActorNameInput } from '@safetyhub/shared';
import {
  Badge,
  Button,
  buttonClassName,
  Card,
  FILTER_SELECT_CLASS,
  Modal,
  PAGER_BUTTON_CLASS,
  TABLE_HEADER_CELL_CLASS,
} from '@safetyhub/ui';
import { getReportTypeTileClass } from '../../components/reports/reportTypeDisplay';
import { StatTile } from '../../components/StatTile';
import { FilterPill } from '../../components/FilterPill';
import { SearchHotkeyBadge, useSearchHotkey } from '../../components/SearchHotkey';
import {
  DOCUMENT_TABS,
  DocumentTab,
  expandStatusQueryScope,
  getDocumentTabCount,
  getDocumentTabScope,
  resolveActiveDocumentTab,
} from './documentTabs';
import { buildPagerRange } from './pagerRange';
import { resolveDeleteDecision, type DeleteDecision } from './deletePermission';
import { isTbmEducationDocument } from './postSubmitFlows';
import { filterLatestRoundSignatures } from './hooks/reportHelpers';
import { formatDayDistance } from '../../utils/relativeDay';
import { ReportDropdownMenu } from '../../components/reports/ReportDropdownMenu';
import { ReportDeleteConfirmDialog } from '../../components/reports/ReportDeleteConfirmDialog';
import { invalidateSiblingReports } from '../../components/reports/useSiblingReports';
import { ShareReportModal } from '../../components/reports/ShareReportModal';
import SiteFormModal from '../../components/modals/SiteFormModal';
import CreateInspectionModal from '../../components/modals/CreateInspectionModal';
import { Report } from '../../lib/reports';
import { DocStatusBadge } from './DocStatusBadge';
import {
  PlusIcon,
  AlertTriangleIcon as ExclamationIcon,
  ChevronDownIcon as ChevronIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUturnLeftIcon,
  BuildingIcon,
  CheckCircleIcon,
  CheckSquareIcon,
  ClockIcon,
  DocumentIcon,
  ListLinesIcon,
  MapPinIcon,
  PencilIcon,
  SearchIcon,
  UsersIcon,
} from '../../components/icons';

/**
 * 보고서 목록 API author({id, member:{name}, deletedAt}) → ActorNameInput 변환.
 * 스냅샷 없는 DRAFT/미결재 보고서의 라이브 작성자 이름을 표시하기 위해 사용.
 */
function toListAuthorActor(author: Report['author'] | null | undefined): ActorNameInput | null {
  if (!author) return null;
  return {
    name: author.member?.name ?? author.name ?? '',
    deletedAt: author.deletedAt,
  };
}

// 데스크톱 리스킨 공통 클래스(필터 셀렉트·표 헤더 셀·sticky 미적용 근거)는
// `@safetyhub/ui`의 controlClasses로 승격했다 — 현장/멤버 목록과 같은 문자열을 세 벌로
// 들고 있던 것을 한 곳으로 모은 것.

/** 검색 입력이 멎고 조회로 넘어가기까지. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * 목록 상태 중 URL이 쥐는 것 — 필터 4축 + 검색어(페이지는 `listPageParam`이 따로 쥔다).
 *
 * URL에 올리는 이유는 `?page=`와 같다: 상세로 갔다 돌아오면(back-link·뒤로가기) 컴포넌트가
 * 다시 마운트되므로, URL에 없는 상태는 전부 초기값으로 떨어진다. 종전에도 탭(=유형)만은
 * `?tab=`으로 올려 두고 있었고, 그 자리를 축별로 넓힌 것이다.
 *
 * 값이 비면 키를 지운다 — 기본값을 URL에 남기지 않아야 `/reports`와
 * `/reports?templateKey=&status=`가 갈라지지 않는다(`writePageParam`과 같은 규약).
 *
 * 「유형」 축은 `?templateKey=`(양식 키)다 — 저장용 `type`은 전부 `COMPOSED`라 유형을 가르지
 * 못한다(`documentTemplateFilter.ts`). 옛 `?type=`·`?tab=tbm`은 읽지 않고 지운다.
 */
const LIST_QUERY_KEYS = [TEMPLATE_KEY_PARAM, 'siteId', 'status', 'mine', 'q'] as const;

/**
 * 진입 URL이 지정한 상태 — 아는 값만 받는다.
 *
 * 양식 키와 달리 걸러 내는 이유는 서버의 반응이 달라서다: 양식 키는 모르는 값이면 빈 목록이지만,
 * 상태는 enum DTO라 이상한 값이 400으로 돌아와 목록 자리에 에러 배너가 뜬다. 손으로 URL을
 * 고쳤을 때 화면이 깨지는 대신 조용히 '전체 상태'로 열리는 편이 낫다.
 */
const ENTRY_STATUSES = [
  'CREATING',
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
] as const satisfies readonly ReportStatus[];

function resolveEntryStatus(params: URLSearchParams): ReportStatus | '' {
  const status = params.get('status');
  return ENTRY_STATUSES.find((known) => known === status) ?? '';
}

/**
 * 검색 입력 — 목업 `.search`(돋보기 + borderless 입력). 멤버 관리 툴바와 같은 골격이다.
 *
 * 목업의 `⌘ K` 뱃지는 넣지 않았다 — 전역 검색 단축키가 아직 없어, 뱃지만 두면 눌러도
 * 아무 일이 없는 표시가 된다.
 *
 * 검색은 **서버 조회**다(멤버 관리는 로드된 목록의 클라이언트 필터라 즉시 반영해도
 * 되지만, 여기는 페이지네이션된 목록이라 서버가 전체에서 찾아야 한다). 그래서 입력값과
 * 조회어를 갈라 두고 호출부가 debounce한다 — 이 컴포넌트는 입력만 담당한다.
 */
function SearchField({
  value,
  onChange,
  className,
  inputRef,
  showHotkey = false,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** ⌘K 포커스 대상(데스크톱 툴바만 전달 — 페이지의 주 검색창 하나여야 한다). */
  inputRef?: React.Ref<HTMLInputElement>;
  /** 시안 `.kbd` 단축키 배지 표시 여부 — inputRef를 전달한 쪽만 켠다. */
  showHotkey?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded-control border border-line bg-surface-soft px-3 transition-colors focus-within:border-brand-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-brand-50 ${className ?? ''}`}
    >
      <SearchIcon className="h-4 w-4 flex-none text-ink-400" />
      <input
        ref={inputRef}
        type="search"
        aria-label="문서 검색"
        placeholder="문서명, 작성자, 현장으로 검색"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 bg-transparent text-[15px] text-ink-900 outline-none placeholder:text-ink-400"
      />
      {showHotkey && <SearchHotkeyBadge />}
    </div>
  );
}

export default function ReportsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentMembership, user } = useAuthStore();
  // 조직 RBAC 판정(P6 판정 이관 ③) — 옛 `currentMembership?.role === 'OWNER'` 자리.
  const { can } = usePermissions();
  const { subscribe, isConnected, isAuthenticated } = useWebSocketStore();
  const { showToast } = useToastStore();
  const currentGroupId = useGroupStore((s) => s.currentGroupId);
  const companyId = currentMembership?.companyId;

  /**
   * 「유형」 필터의 선택지 재료 — 지금 이 사용자에게 켜진 Composer 양식(`documentTemplateFilter`).
   * 로딩 중에는 빈 배열이라 pill 선택지가 잠시 비지만, 고른 값(`templateKey`)은 파생이 되살려
   * 붙이므로 표시가 거짓이 되지는 않는다(로딩 동안에도 「전체」로 보이지 않는다).
   */
  const { templates: composerTemplates } = useEnabledComposerTemplates(companyId);

  // 디바이스 레이아웃 감지
  const layout = useDeviceLayout();
  const isMobile = layout === 'phone' || layout === 'fold';

  // 필터 상태 — 초기값은 진입 URL에서 읽는다(`LIST_QUERY_KEYS` 주석).
  const [siteId, setSiteId] = useState(() => searchParams.get('siteId') ?? '');
  /** 「유형」 = 양식 키(`report_templates.key`). 빈 문자열이 전체. */
  const [templateKey, setTemplateKey] = useState(() => resolveEntryTemplateKey(searchParams));
  const [status, setStatus] = useState<ReportStatus | ''>(() => resolveEntryStatus(searchParams));
  /** '내가 작성' 탭 — 상태와 함께 탭이 소유하는 축(`documentTabs`). */
  const [mine, setMine] = useState(() => searchParams.get('mine') === '1');

  /** 검색 입력값(즉시)과 조회에 쓰는 값(debounce 후)을 갈라 둔다 — 타자마다 조회하지 않게. */
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') ?? '');
  // ⌘K/Ctrl+K → 데스크톱 툴바 검색창 포커스(시안 .kbd 배지와 짝).
  const searchRef = useSearchHotkey();
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');

  const activeTab = resolveActiveDocumentTab({ status, mine });

  // 페이지네이션 — 로컬 state가 아니라 URL 쿼리(`?page=`)에 둔다.
  // 로컬 state면 상세로 갔다 뒤로가기로 돌아올 때 언마운트와 함께 사라져 항상 1페이지로
  // 리셋된다. URL에 있으면 히스토리 복귀만으로 보던 페이지가 그대로 살아난다.
  const page = readPageParam(searchParams);

  // setPage는 기존 시그니처(숫자 | 갱신함수)를 그대로 유지한다 — 필터·유형·탭 변경 시의
  // `setPage(1)` 리셋 호출 지점(10여 곳)이 수정 없이 그대로 동작해야 한다.
  const setPage = useCallback(
    (next: number | ((prev: number) => number)) => {
      setSearchParams(
        // 스냅샷이 아니라 최신 params 기준으로 갱신한다 — 같은 배치에서 일어나는
        // 탭 동기화와 서로의 쿼리를 덮어쓰지 않게.
        (prev) => writePageParam(prev, typeof next === 'function' ? next(readPageParam(prev)) : next),
        // replace — 페이지 이동마다 히스토리를 쌓으면 목록에서 뒤로가기가 페이지를 거슬러
        // 오르느라 이전 화면으로 못 빠져나온다(로컬 state이던 기존 동작과도 어긋난다).
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // 그룹을 바꾸면 현장 필터를 비운다 — 현장은 그룹에 속하므로, 이전 그룹의 siteId가 남으면
  // 서버가 group∩site 교집합으로 0건을 주고 드롭다운도 선택값을 못 그린다. 최초 마운트(딥링크
  // siteId 포함)는 건너뛰고 실제 전환에서만 초기화한다.
  const prevGroupIdRef = useRef(currentGroupId);
  useEffect(() => {
    if (prevGroupIdRef.current === currentGroupId) return;
    prevGroupIdRef.current = currentGroupId;
    setSiteId('');
    setPage(1);
  }, [currentGroupId, setPage]);

  // 필터·검색어를 URL에 반영. 옛 유형 축 키(`?type=`·`?tab=`)는 읽지 않았으니 여기서 지운다 —
  // 옛 북마크는 조용히 전체 목록으로 열리고 URL도 그 사실대로 정리된다.
  //
  // 쓸 게 있을 때만 쓴다 — 무조건 setSearchParams를 부르면 navigate → 새 location →
  // 이 effect 재실행이 맞물려 URL이 같은데도 계속 돌 수 있다.
  //
  // replace인 이유는 `?page=`와 같다: 필터를 만질 때마다 히스토리가 쌓이면 목록에서
  // 뒤로가기가 자기 자신을 거슬러 오르느라 이전 화면으로 빠져나가지 못한다. 대신 필터는
  // 뒤로가기로 되돌릴 수 없으므로, URL→state 역방향 동기화도 필요 없다(히스토리에 남는
  // 필터 항목이 애초에 없다 — 남는 건 상세를 다녀올 때 복원되는 마지막 URL뿐이다).
  useEffect(() => {
    const desired: Record<(typeof LIST_QUERY_KEYS)[number], string> = {
      [TEMPLATE_KEY_PARAM]: templateKey,
      siteId,
      status,
      mine: mine ? '1' : '',
      q: search,
    };
    const isStale =
      LEGACY_TYPE_PARAMS.some((key) => searchParams.has(key)) ||
      LIST_QUERY_KEYS.some((key) => (searchParams.get(key) ?? '') !== desired[key]);
    if (!isStale) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        LEGACY_TYPE_PARAMS.forEach((key) => next.delete(key));
        LIST_QUERY_KEYS.forEach((key) => {
          if (desired[key]) next.set(key, desired[key]);
          else next.delete(key);
        });
        return next;
      },
      { replace: true },
    );
  }, [templateKey, siteId, status, mine, search, searchParams, setSearchParams]);

  // 검색 debounce — 입력이 멎고 300ms 뒤에만 조회 조건을 바꾼다. 값이 실제로 달라질 때만
  // 페이지를 1로 되돌린다(첫 렌더에서 둘 다 빈 문자열이면 타이머 자체가 걸리지 않는다).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === search) return;
    const timer = setTimeout(() => {
      setSearch(trimmed);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search, setPage]);

  /** 탭 = 상태·mine 두 축을 한 번에 세팅한다(`documentTabs`가 대응 규칙의 정본). */
  const selectTab = (tab: DocumentTab) => {
    const scope = getDocumentTabScope(tab);
    setStatus(scope.status);
    setMine(scope.mine);
    setPage(1);
  };

  // 삭제 관련 상태
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<{
    id: string;
    title: string;
    status: ReportStatus;
    authorId: string;
    authorName: string;
    siteName?: string;
    /**
     * 삭제 모달의 라벨·경고 문구가 목록 배지와 같은 축에서 파생하도록 서명 행을 함께 들고 있는다.
     * 목록 응답(`GET /reports`)도 `approvalSignatures`를 싣는다(findAll include → `...report`).
     */
    approvalSignatures?: unknown[] | null;
    /**
     * 삭제 경고의 활동 축 — 「교육을 시작한 적 있나」. 목록 응답(`GET /reports`)이 `_count`에서
     * 파생해 싣는다(2026-09-08 `tbm-status-model-two-axes`: 구 `status === 'SIGNING'` 대체).
     * 안 실리면 대화상자가 「모르면 강하게」로 판정하므로 여기서 0으로 채우지 말 것.
     */
    educationSessionCount?: number;
    /**
     * 최신 라운드에 결재 승인이 하나라도 있나 — 대화상자의 **경고 강도** 축이다.
     *
     * ⚠️ 삭제 «권한» 축(제3자 판정)보다 **넓다**(작성자 칸 서명도 센다). 상세 두 화면이 같은
     * 식으로 파생하며, 목록이 이 값을 안 주면 대화상자가 결재가 도는 문서를 「이 보고서를
     * 삭제하시겠습니까?」 단순 확인으로 지운다. 종전엔 목록에서 그런 문서에 애초에 삭제
     * 버튼이 안 떴으므로(Owner 전용) 드러나지 않았을 뿐이다(2026-09-09).
     */
    hasApprovalInProgress?: boolean;
    /**
     * 이어질 흐름의 선언 키 — 대화상자의 **활동 축**(`isTBM || 선언`)과 라벨 파생이 함께 읽는다.
     * 목록 응답엔 스냅샷이 없으므로 이 스칼라가 유일한 선언 축이다. 안 주면 COMPOSED TBM이
     * 목록 대화상자에서만 활동 축을 잃어, 교육 서명이 남은 문서가 단순 확인으로 지워진다.
     */
    postSubmitFlowKey?: string | null;
    requiresApproval?: boolean;
    finalApproverName?: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 삭제 요청 취소 관련 상태
  const [showCancelDeleteRequestDialog, setShowCancelDeleteRequestDialog] = useState(false);
  const [cancelDeleteTarget, setCancelDeleteTarget] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [isCancellingDeleteRequest, setIsCancellingDeleteRequest] = useState(false);

  // 공유 모달 상태
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareReportId, setShareReportId] = useState<string | null>(null);
  const [shareReportTitle, setShareReportTitle] = useState<string>('');

  // PDF 상태 캐시 (보고서 ID -> PDF 상태)
  const [pdfStatusCache, setPdfStatusCache] = useState<Record<string, PdfStatus>>({});

  // PDF 상태 모달
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfModalState, setPdfModalState] = useState<{
    reportId: string;
    reportTitle: string;
    status: PdfStatus;
  } | null>(null);

  const limit = 10;

  // 모바일 필터 토글
  const [showFilters, setShowFilters] = useState(false);

  // React Query로 현장 목록 조회
  const { data: sites = [] } = useSites(companyId, currentGroupId);

  // React Query로 문서 목록 조회 (통합)
  const {
    data: reportsData,
    isLoading,
    error: queryError,
  } = useReports(companyId, {
    siteId: siteId || undefined,
    groupId: currentGroupId,
    // 초안 스코프는 DRAFT+CREATING 집합으로 질의한다(❓4 — state·URL은 단일 유지).
    status: expandStatusQueryScope(status),
    templateKey: templateKey || undefined,
    search: search || undefined,
    mine: mine || undefined,
    limit,
    offset: (page - 1) * limit,
  });

  const reports = reportsData?.data || [];
  const totalCount = reportsData?.meta?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // 통계 카드 — 목록과 별개로 읽는 보조 정보다(결재함 loadStats와 같은 규약).
  // 실패해도 목록은 그대로 쓰게 두고 카드·탭 배지만 비운다. 현장 필터는 통계에도 걸지만
  // 유형·상태·검색은 걸지 않는다 — 카드가 "지금 목록"이 아니라 "전체 분포"를 세는 자리라
  // 카드를 눌러 목록을 좁혀도 카드 값은 그대로여야 한다.
  const [stats, setStats] = useState<DocumentStats | null>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    getDocumentStats(companyId, { siteId: siteId || undefined, groupId: currentGroupId })
      .then((next) => {
        if (!cancelled) setStats(next);
      })
      .catch((err) => {
        if (!cancelled) setStats(null);
        console.error('Failed to load document stats:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, siteId, currentGroupId, reportsData]);

  // 에러 메시지 처리
  const error = queryError instanceof Error ? queryError.message : '';

  // 보고서 삭제/업데이트 이벤트 구독 (목록 자동 갱신)
  useEffect(() => {
    if (!isConnected || !isAuthenticated) return;
    const unsubscribeDeleted = subscribe<ReportDeletedPayload>(
      ServerEvents.REPORT_DELETED,
      (payload) => {
        console.log('[ReportsList] REPORT_DELETED event received:', payload);
        queryClient.refetchQueries({ queryKey: ['reports'] });
      }
    );
    const unsubscribeUpdated = subscribe<ReportEventPayload>(
      ServerEvents.REPORT_UPDATED,
      (payload) => {
        console.log('[ReportsList] REPORT_UPDATED event received:', payload);
        queryClient.refetchQueries({ queryKey: ['reports'] });
      }
    );
    const unsubscribeDeleteResolved = subscribe<ReportDeleteRequestResolvedPayload>(
      ServerEvents.REPORT_DELETE_REQUEST_RESOLVED,
      (payload) => {
        if (payload.action === 'APPROVED') {
          showToast(`"${payload.reportTitle}" 보고서가 ${payload.approverName}님의 승인으로 삭제되었습니다.`, 'info');
        } else {
          showToast(`"${payload.reportTitle}" 보고서 삭제 요청이 ${payload.approverName}님에 의해 거절되었습니다.`, 'info');
        }
        queryClient.refetchQueries({ queryKey: ['reports'] });
      }
    );
    return () => {
      unsubscribeDeleted();
      unsubscribeUpdated();
      unsubscribeDeleteResolved();
    };
  }, [isConnected, isAuthenticated, subscribe, queryClient, showToast]);

  // 보고서 생성/삭제 알림 이벤트 구독 (조직 방으로 브로드캐스트되는 이벤트)
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !companyId) return;

    const unsubscribeCreated = subscribe<ReportCreatedNotifyPayload>(
      ServerEvents.REPORT_CREATED_NOTIFY,
      (payload) => {
        if (payload.companyId === companyId) {
          queryClient.refetchQueries({ queryKey: ['reports'] });
        }
      }
    );

    const unsubscribeDeleted = subscribe<ReportDeletedNotifyPayload>(
      ServerEvents.REPORT_DELETED_NOTIFY,
      (payload) => {
        if (payload.companyId === companyId) {
          queryClient.refetchQueries({ queryKey: ['reports'] });
        }
      }
    );

    // TBM 서명 이벤트 구독 (서명 완료 수 실시간 갱신)
    const unsubscribeTbmSigned = subscribe<TbmEventPayload>(
      ServerEvents.TBM_ATTENDEE_SIGNED,
      (payload) => {
        if (payload.companyId === companyId) {
          queryClient.refetchQueries({ queryKey: ['reports'] });
        }
      }
    );

    // TBM 상태 변경 이벤트 구독 (배포·결재 등 문서 축 전이).
    // ⚠️ 교육 완료도 여기로 온다 — 서버는 `emitEducationChanged`로 TBM_UPDATED를 쏜다.
    // 종전에 나란히 있던 `TBM_COMPLETED` 구독은 **서버가 emit하지 않는 이벤트**라 한 번도
    // 발화하지 않았고(2026-09-13 확인, apps/server 기준 0건), 이 핸들러와 본문·게이트가 같아
    // 덮이는 것도 없었다. 다시 필요해지면 구독이 아니라 emit부터 놓을 것.
    const unsubscribeTbmUpdated = subscribe<TbmEventPayload>(
      ServerEvents.TBM_UPDATED,
      (payload) => {
        if (payload.companyId === companyId) {
          queryClient.refetchQueries({ queryKey: ['reports'] });
        }
      }
    );

    return () => {
      unsubscribeCreated();
      unsubscribeDeleted();
      unsubscribeTbmSigned();
      unsubscribeTbmUpdated();
    };
  }, [isConnected, isAuthenticated, companyId, subscribe, queryClient]);

  // 승인된 보고서의 PDF 상태 로드
  useEffect(() => {
    if (!companyId || !reports.length) return;

    const approvedReports = reports.filter((r) => r.status === 'APPROVED');
    approvedReports.forEach(async (report) => {
      if (pdfStatusCache[report.id]) return;

      try {
        const pdfResult = await getPdfStatus(report.id, companyId);
        const pdfStatusValue: PdfStatus =
          pdfResult.status === 'completed' ? 'COMPLETED' :
          pdfResult.status === 'processing' ? 'GENERATING' :
          pdfResult.status === 'queued' ? 'PENDING' :
          pdfResult.status === 'failed' ? 'FAILED' : 'NONE';

        setPdfStatusCache((prev) => ({ ...prev, [report.id]: pdfStatusValue }));
      } catch {
        setPdfStatusCache((prev) => ({ ...prev, [report.id]: 'NONE' }));
      }
    });
  }, [companyId, reports, pdfStatusCache]);

  /**
   * 삭제 판정 — 서버(`ReportDeleteService`)·상세(`useReportPermissions`)와 **같은 한 벌**을 부른다.
   *
   * ⚠️ 종전에 여기만 축이 달랐다: `type + status`로 「결재 중/완료는 Owner만」이라 **서버보다
   * 엄격**했고, 그 과잉 제한을 `type === 'TBM'` 특례가 되풀어 줬다. 서버는 그 두 상태에서도
   * 작성자를 허용하므로(제3자 결재가 붙었으면 삭제 «요청» 경로) 특례는 「TBM은 예외」가 아니라
   * **자기가 만든 제한을 자기가 푼 것**이었고, 축을 맞추자 넓힐 것 없이 사라졌다
   * (2026-09-09 — 결정 `2026-09-08-tbm-status-model-two-axes` [업데이트] §(A)).
   */
  const resolveDocDeleteDecision = (report: {
    authorId: string;
    status: ReportStatus;
    siteId?: string;
    hasThirdPartyApproval?: boolean;
  }): DeleteDecision =>
    resolveDeleteDecision({
      // P6 ③: 옛 `role === 'OWNER'` → 권한 key 둘(자격 / 제3자 서명 우회). 상세와 **같은 한 벌**이므로
      // 여기서도 같은 두 키를 넘긴다 — 한쪽만 키로 옮기면 두 surface의 축이 다시 갈린다.
      canDelete: can('report.delete'),
      canDeleteApproved: can('report.delete-approved'),
      isAuthor: report.authorId === user?.id,
      status: report.status,
      // 현장이 없는 문서는 현장 제약이 없다(종전 `report.siteId &&` 가드와 같은 뜻).
      siteAssigned:
        !report.siteId || isSiteAssigned(currentMembership ?? null, report.siteId),
      // 서버 파생 «문서 사실». 안 실렸으면(구 응답) 없음으로 본다 — 즉시 삭제를 시도하고
      // 서버가 최종 방어선이다(종전에도 목록은 이 축을 아예 안 봤다).
      hasThirdPartyApproval: report.hasThirdPartyApproval === true,
    });

  // 삭제 권한 확인 — 드롭다운 메뉴가 읽는 `{allowed, reason}` 형상. 문구는 이 화면이 소유한다.
  const canDeleteDoc = (report: {
    authorId: string;
    status: ReportStatus;
    hasPendingDeleteRequest?: boolean;
    siteId?: string;
    hasThirdPartyApproval?: boolean;
  }) => {
    const decision = resolveDocDeleteDecision(report);
    if (decision.kind === 'site-not-assigned') {
      return { allowed: false, reason: '담당 현장의 문서만 삭제할 수 있습니다.' };
    }
    if (decision.kind === 'no-delete-permission') {
      // 종전 문구는 'Owner 또는 작성자만 삭제 가능'이었다 — 판정이 역할이 아니라 권한 key가 된
      // P6 ③부터 그 어휘는 거짓이다(같은 이유로 상세는 이미 역할을 말하지 않았다).
      return { allowed: false, reason: '문서 삭제 권한이 없습니다.' };
    }
    return { allowed: true, reason: '' };
  };

  const handleDeleteClick = (report: typeof reports[0]) => {
    // 삭제 요청 필요 판정은 **서버 파생 사실**(`hasThirdPartyApproval`)을 actor·status와
    // 조합해 얻는다 — 제3자 판정 자체는 서버가 공용 유틸로 하고(author 단계·미지정 칸·
    // 작성자 본인 서명 제외), 조합만 여기서. 프론트 재파생 금지는 그대로다(2026-08-17).
    //
    // ⚠️ 종전엔 `requiresDeleteApproval`을 그대로 읽었는데 그 필드는 **APPROVED 한정**이라,
    // `PENDING_APPROVAL`의 작성자(제3자 결재 1건+)가 요청이 아니라 **직접 삭제**로 유도돼
    // 서버가 거부한다 — 눌리는데 실패하는 버튼이 된다(2026-09-09).
    const decision = resolveDocDeleteDecision(report);
    const requiresApproval = decision.kind === 'allowed' && decision.requiresApproval;
    const finalApproverName = report.deleteApproverName ?? undefined;

    setReportToDelete({
      id: report.id,
      title: report.title,
      status: report.status,
      authorId: report.authorId,
      authorName: getEvidenceDisplayName(report.authorSignerName ?? report.authorSnapshotName ?? null, toListAuthorActor(report.author), '-'),
      siteName: report.site?.name,
      approvalSignatures: report.approvalSignatures,
      educationSessionCount: report.educationSessionCount,
      // 상세 두 화면(`[id].tsx`·`ComposerReportDetail`)과 **같은 식**으로 파생한다.
      hasApprovalInProgress: filterLatestRoundSignatures(
        report.approvalSignatures ?? [],
      ).some((sig) => sig.status === 'APPROVED'),
      postSubmitFlowKey: report.postSubmitFlowKey,
      requiresApproval,
      finalApproverName,
    });
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete || !companyId) return;

    setIsDeleting(true);
    try {
      // 구 `type='TBM'` 행의 `deleteTBM` 분기는 걷었다 — 그 행이 0건이라(전부 COMPOSED로 전환)
      // 삭제는 유형과 무관하게 한 엔드포인트다.
      await deleteReport(reportToDelete.id, companyId);
      showToast('보고서가 삭제되었습니다.', 'success');
      setShowDeleteDialog(false);
      setReportToDelete(null);
      queryClient.refetchQueries({ queryKey: ['reports'] });
      // 형제 탭 캐시도 버린다 — 목록은 이 훅을 쓰지 않지만 **지워진 문서가 남는 자리는 같다**
      // (한 건을 지우고 같은 묶음의 다른 문서로 들어가면 그 탭이 그대로 뜬다 — 2026-09-10
      // 사용자 적발, 상세 경로와 같은 결함). 키를 아는 책임은 `useSiblingReports` 모듈이다.
      invalidateSiblingReports(queryClient);
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRequestDelete = async (reason: string) => {
    if (!reportToDelete || !companyId) return;

    setIsDeleting(true);
    try {
      const result = await requestReportDelete(reportToDelete.id, companyId, reason);
      setShowDeleteDialog(false);
      setReportToDelete(null);
      showToast(
        result.hasContact
          ? `${result.approverName}님에게 삭제 승인 요청이 전송되었습니다.`
          : `삭제 승인 요청이 생성되었습니다. ${result.approverName}님의 연락처가 등록되지 않아 직접 전달해주세요.`,
        result.hasContact ? 'success' : 'info',
      );
      queryClient.refetchQueries({ queryKey: ['reports'] });
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDeleteRequestClick = (report: { id: string; title: string }) => {
    setCancelDeleteTarget({ id: report.id, title: report.title });
    setShowCancelDeleteRequestDialog(true);
  };

  const closeCancelDeleteRequestDialog = () => {
    setShowCancelDeleteRequestDialog(false);
    setCancelDeleteTarget(null);
  };

  const handleCancelDeleteRequestConfirm = async () => {
    if (!cancelDeleteTarget || !companyId) return;

    setIsCancellingDeleteRequest(true);
    try {
      await cancelReportDeleteRequest(cancelDeleteTarget.id, companyId);
      showToast('삭제 요청이 취소되었습니다.', 'success');
      setShowCancelDeleteRequestDialog(false);
      setCancelDeleteTarget(null);
      queryClient.refetchQueries({ queryKey: ['reports'] });
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsCancellingDeleteRequest(false);
    }
  };

  const handlePrint = async (reportId: string, reportTitle: string) => {
    if (!companyId) return;

    const cachedStatus = pdfStatusCache[reportId];

    if (cachedStatus === 'COMPLETED') {
      try {
        await downloadPdf(reportId, companyId, reportTitle);
      } catch (err) {
        const apiError = extractApiError(err);
        showToast(apiError.message, 'error');
      }
    } else {
      try {
        const pdfResult = await getPdfStatus(reportId, companyId);
        const pdfStatusValue: PdfStatus =
          pdfResult.status === 'completed' ? 'COMPLETED' :
          pdfResult.status === 'processing' ? 'GENERATING' :
          pdfResult.status === 'queued' ? 'PENDING' :
          pdfResult.status === 'failed' ? 'FAILED' : 'NONE';

        setPdfStatusCache((prev) => ({ ...prev, [reportId]: pdfStatusValue }));

        if (pdfStatusValue === 'COMPLETED') {
          await downloadPdf(reportId, companyId, reportTitle);
        } else {
          setPdfModalState({ reportId, reportTitle, status: pdfStatusValue });
          setShowPdfModal(true);
        }
      } catch (err) {
        const apiError = extractApiError(err);
        showToast(apiError.message, 'error');
      }
    }
  };

  const handleShareClick = (reportId: string, reportTitle: string) => {
    setShareReportId(reportId);
    setShareReportTitle(reportTitle);
    setShowShareModal(true);
  };

  /**
   * 현재 목록 URL(쿼리 포함) — 상세의 back-link으로 넘겨 화면 안 "목록" 버튼도
   * 보던 페이지·필터·탭으로 돌아오게 한다. 브라우저 뒤로가기는 히스토리가 알아서 복원하지만,
   * 상세 헤더의 뒤로가기는 이 값이 없으면 type 기본값(`/reports`)이라 1페이지로 떨어진다.
   * fromLabel은 넘기지 않는다 — 라벨은 보고서 type 기본값을 그대로 쓴다.
   */
  const listEntryState = { from: `/reports${searchParams.toString() ? `?${searchParams}` : ''}` };

  /** 행 클릭 시 상세 페이지로 이동 (TBM 포함 모든 유형이 /reports/:id 디스패처로 통합) */
  const navigateToDetail = (report: { id: string }) => {
    navigate(`/reports/${report.id}`, { state: listEntryState });
  };

  if (!companyId) {
    return (
      <div className="text-center py-12">
        <p className="font-medium text-ink-500">조직을 선택해주세요.</p>
      </div>
    );
  }

  // 생성 CTA — 유형 필터가 TBM일 때 구 TBM 작성으로 직행하던 분기(`documentCreateCta`)는
  // 구 TBM 진입로와 함께 걷었다. 모든 문서가 Composer 양식이라 목적지는 하나다.
  const createLink = '/reports/new';
  const createLabel = '보고서 생성';

  /**
   * 「유형」 필터 선택지 — 활성 Composer 양식에서 파생한다(`documentTemplateFilter`).
   * 고른 키가 선택지에 없으면(비활성화된 양식·옛 북마크) 그 키만 되살려 붙여 pill이 「전체」라
   * 거짓말하지 않게 한다 — 새로 고를 수는 없다.
   */
  const filterTemplateOptions = resolveTemplateFilterOptions(composerTemplates, templateKey);

  // 상태 필터 옵션 — 유형별 어휘(구 TBM의 '생성됨')는 구 `type` 축과 함께 걷었다.
  // 배지 라벨(`resolveReportStatusLabel`)도 같은 정본을 쓰므로 여기 문구는 그 낱말들을 나열한다.
  const statusOptions: { value: ReportStatus; label: string }[] = [
    { value: 'CREATING', label: '생성 중' },
    // 이 한 값(DRAFT)이 배지에서 두 낱말로 갈린다 — 초안 / 진행 중(이어질 흐름의 설정이 끝난
    // 문서, `isPostSubmitFlowInProgress`). 값·질의 스코프는 그대로 두고 문구만 그 사실에
    // 맞춘다: 배지가 「진행 중」이라 말하는데 필터가 「초안」만 말하면 고를 수 있는 것과 보이는
    // 것이 어긋난다.
    { value: 'DRAFT', label: '초안/진행 중' },
    { value: 'PENDING_APPROVAL', label: '결재 대기' },
    { value: 'APPROVED', label: '완료/결재완료' },
    { value: 'REJECTED', label: '반려됨' },
  ];

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      {/* 헤더 */}
      {isMobile ? (
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold tracking-tight text-ink-900">문서관리</h1>
          <div className="flex gap-2">
            <Link
              to={createLink}
              className="p-2 bg-brand-600 text-white rounded-control"
            >
              <PlusIcon className="h-5 w-5" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex items-end justify-between gap-6">
          <div>
            <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">문서관리</h1>
            <p className="mt-1 text-[15px] font-medium text-ink-500">
              보고서와 TBM을 통합 관리합니다.
            </p>
          </div>
          {/* Link 시맨틱 유지(가운데 클릭·Ctrl+클릭 새 탭) — 버튼 시각은 buttonClassName으로 빌림. */}
          <Link to={createLink} className={buttonClassName('primary')}>
            <PlusIcon className="h-5 w-5" />
            {createLabel}
          </Link>
        </div>
      )}

      {/* 통계 카드 4장 — 각 카드가 대응 상태 탭의 바로가기다. 모바일은 2×2(결재함과 같은 격자).
          값을 못 읽었을 때 0으로 속이지 않고 '—'로 두는 것도 결재함 카드와 같은 규약이다. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="전체 문서"
          value={stats ? String(stats.total) : '—'}
          unit={stats ? '건' : undefined}
          foot={
            stats && stats.createdThisMonth > 0
              ? `이번 달 ${stats.createdThisMonth}건 신규 작성`
              : undefined
          }
          // 변화가 없으면(0) pill이 눈만 끌고 뜻이 없다 — 서버가 비교 대상이 없을 땐 null을 준다.
          pill={
            stats && stats.createdChangePercent != null && stats.createdChangePercent !== 0
              ? { kind: 'diff', diff: stats.createdChangePercent, suffix: '%' }
              : undefined
          }
          icon={DocumentIcon}
          accentClass="text-brand-600"
          iconBgClass="bg-brand-50"
          selected={activeTab === 'all'}
          onSelect={() => selectTab('all')}
        />
        {/*
          이 버킷(DRAFT+CREATING)에는 이어질 흐름이 도는 문서도 있다 — 그 배지는 「진행 중」이라
          종전 문구 「초안 상태로 대기 중」은 거짓이 됐다. 버킷 자체는 **문서 축**(결재 상신 전)
          이므로 그 축의 말로 적는다. 버킷 경계(질의 스코프)는 그대로다.
        */}
        <StatTile
          label="작성 중"
          value={stats ? String(stats.draft) : '—'}
          unit={stats ? '건' : undefined}
          foot="결재 상신 전"
          icon={PencilIcon}
          accentClass="text-ink-500"
          iconBgClass="bg-ink-100"
          selected={activeTab === 'draft'}
          onSelect={() => selectTab('draft')}
        />
        <StatTile
          label="결재 대기"
          value={stats ? String(stats.pendingApproval) : '—'}
          unit={stats ? '건' : undefined}
          foot="결재자 응답 대기 중"
          // 증감 pill 없음 — 대기 수는 "지금 쌓여 있는 양"이라 어제와 비교하려면 어제 시점의
          // 스냅샷이 있어야 한다(결재함 '내 결재 대기' 카드와 같은 사유).
          icon={ClockIcon}
          accentClass="text-amber-600"
          iconBgClass="bg-amber-50"
          selected={activeTab === 'pending'}
          onSelect={() => selectTab('pending')}
        />
        <StatTile
          label="결재 완료"
          value={stats ? String(stats.approved) : '—'}
          unit={stats ? '건' : undefined}
          foot={
            stats && stats.approvedLast7Days > 0
              ? `최근 7일 ${stats.approvedLast7Days}건 승인`
              : undefined
          }
          pill={
            stats && stats.approvedChangePercent != null && stats.approvedChangePercent !== 0
              ? { kind: 'diff', diff: stats.approvedChangePercent, suffix: '%' }
              : undefined
          }
          icon={CheckSquareIcon}
          accentClass="text-emerald-600"
          iconBgClass="bg-emerald-50"
          selected={activeTab === 'approved'}
          onSelect={() => selectTab('approved')}
        />
      </div>

      {/* 필터 */}
      {isMobile ? (
        <Card padding="none" className="overflow-hidden">
          {/* 좁은 화면에서도 검색은 접지 않는다 — 목록을 좁히는 가장 빠른 수단이라
              필터 아코디언 안에 숨기면 한 번 더 눌러야 닿는다. */}
          <div className="border-b border-line p-3">
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              className="h-10 w-full"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full flex items-center justify-between p-3 text-left"
          >
            <span className="text-sm font-semibold text-ink-700">필터</span>
            <div className="flex items-center gap-2">
              {(siteId || templateKey || status) && (
                <Badge tone="brand">
                  {[siteId && '현장', templateKey && '유형', status && '상태'].filter(Boolean).join(', ')}
                </Badge>
              )}
              <ChevronIcon className={`h-5 w-5 text-ink-400 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {showFilters && (
            <div className="p-3 pt-0 space-y-3 border-t border-line">
              <div>
                <label htmlFor="site-mobile" className="block text-xs font-semibold text-ink-500 mb-1">
                  현장
                </label>
                <select
                  id="site-mobile"
                  value={siteId}
                  onChange={(e) => {
                    setSiteId(e.target.value);
                    setPage(1);
                  }}
                  className={`${FILTER_SELECT_CLASS} block w-full`}
                >
                  <option value="">전체 현장</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="type-mobile" className="block text-xs font-semibold text-ink-500 mb-1">
                  유형
                </label>
                <select
                  id="type-mobile"
                  value={templateKey}
                  onChange={(e) => {
                    setTemplateKey(e.target.value);
                    setStatus('');
                    setPage(1);
                  }}
                  className={`${FILTER_SELECT_CLASS} block w-full`}
                >
                  <option value="">전체 유형</option>
                  {filterTemplateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="status-mobile" className="block text-xs font-semibold text-ink-500 mb-1">
                  상태
                </label>
                <select
                  id="status-mobile"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as ReportStatus | '');
                    setPage(1);
                  }}
                  className={`${FILTER_SELECT_CLASS} block w-full`}
                >
                  <option value="">전체 상태</option>
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </Card>
      ) : (
        /* 툴바 — 목업 `.toolbar`(검색 + 필터 pill). 검색은 서버 조회고, pill 3종은
           탭보다 세밀한 축(반려됨 등)까지 고르는 자리다. */
        <Card radius="lg" padding="none" className="flex flex-wrap items-center gap-2.5 px-4 py-3.5">
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            className="h-[38px] min-w-[240px] flex-1"
            inputRef={searchRef}
            showHotkey
          />
          <FilterPill
            icon={BuildingIcon}
            label="현장"
            ariaLabel="현장 필터"
            value={siteId}
            options={sites.map((site) => ({ value: site.id, label: site.name }))}
            onChange={(value) => {
              setSiteId(value);
              setPage(1);
            }}
          />
          {/* 선택지는 활성 Composer 양식에서 파생한다(`documentTemplateFilter`) — 화면에 목록을
              다시 적으면 양식이 늘 때 배지에는 나오는데 필터에는 없는 상태가 된다. */}
          <FilterPill
            icon={ListLinesIcon}
            label="유형"
            ariaLabel="유형 필터"
            value={templateKey}
            options={filterTemplateOptions}
            onChange={(value) => {
              setTemplateKey(value);
              setStatus('');
              setPage(1);
            }}
          />
          <FilterPill
            icon={CheckCircleIcon}
            label="상태"
            ariaLabel="상태 필터"
            value={status}
            options={statusOptions}
            onChange={(value) => {
              setStatus(value as ReportStatus | '');
              setPage(1);
            }}
          />
        </Card>
      )}

      {/* 상태 탭 — 목록 범위의 정본. 탭이 상태·'내가 작성' 두 축을 소유하고, 배지는 검색·유형
          필터와 무관한 전체 기준이다(그래서 목록이 0건이어도 배지는 줄지 않는다). 상태 pill로
          탭에 없는 상태를 고르면 어느 탭도 활성이 아니다(`documentTabs` 주석).
          좁은 화면에선 라벨을 글자 단위로 꺾지 않고(nowrap) 탭바 자체를 가로 스크롤로 흘린다. */}
      <div
        className="flex items-end gap-1 overflow-x-auto border-b border-line"
        role="tablist"
        aria-label="문서 목록 범위"
      >
        {DOCUMENT_TABS.map(({ tab, label }) => {
          const isActive = tab === activeTab;
          const count = getDocumentTabCount(stats, tab);
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => selectTab(tab)}
              className={`-mb-px inline-flex flex-none items-center gap-[7px] whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                isActive
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-ink-500 hover:text-ink-800'
              }`}
            >
              {label}
              {count !== null && (
                <Badge
                  tone={isActive ? 'brand' : 'slate'}
                  size="sm"
                  className="justify-center min-w-[22px] tabular-nums"
                >
                  {count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* 에러 */}
      {error && (
        <div className="rounded-card border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-medium text-rose-700">{error}</p>
        </div>
      )}

      {/* 콘텐츠 */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : reports.length === 0 ? (
        <Card radius="lg" padding="none" className="p-12 text-center">
          <DocumentIcon className="mx-auto h-12 w-12 text-ink-400" />
          {/* 좁혀서 안 나온 것과 애초에 없는 것은 다른 말이다 — 검색어가 있는데 "아직 생성된
              문서가 없습니다"라고 하면 조직에 문서가 없다는 뜻으로 읽히고, 생성 버튼이
              엉뚱한 다음 걸음이 된다. */}
          <h3 className="mt-4 text-lg font-bold text-ink-900">
            {search ? '검색 결과 없음' : '문서 없음'}
          </h3>
          <p className="mt-2 text-sm font-medium text-ink-500">
            {search ? `'${search}'에 해당하는 문서를 찾지 못했습니다.` : '아직 생성된 문서가 없습니다.'}
          </p>
          {!search && (
            <div className="mt-6">
              <Link to={createLink} className={buttonClassName('primary')}>
                <PlusIcon className="h-5 w-5" />
                {createLabel}
              </Link>
            </div>
          )}
        </Card>
      ) : (
        <>
          {isMobile ? (
            <div className="space-y-3">
              {reports.map((report) => (
                <Card
                  key={report.id}
                  className="cursor-pointer"
                  onClick={() => navigateToDetail(report)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold tracking-tight text-ink-900 truncate">
                        {isTbmEducationDocument(report) && (() => { const ts = getTBMTagStyle(report.content?.tag as string); return ts ? <Badge tone={ts.tone} size="sm" className="mr-1">{ts.label}</Badge> : null; })()}
                        {getDocumentDisplayTitle(report)}
                      </h3>
                      <p className="text-xs font-medium text-ink-500 mt-0.5">
                        {getEvidenceDisplayName(report.authorSignerName ?? report.authorSnapshotName ?? null, toListAuthorActor(report.author), '-')}
                        {isTbmEducationDocument(report) && (
                          <span> · 교육담당: {report.distributionTokens?.[0]?.contactName || getEvidenceDisplayName(report.authorSignerName ?? report.authorSnapshotName ?? null, toListAuthorActor(report.author), '-')}</span>
                        )}
                        {isTbmEducationDocument(report) && report.attendeeCount != null && report.attendeeCount > 0 && (
                          <span> · 서명 {report.attendeeCount}명</span>
                        )}
                        {isTbmEducationDocument(report) && (() => {
                          const dur = getTBMDuration(report);
                          return dur ? <span> · {report.completedAt ? dur : `진행 ${dur}`}</span> : null;
                        })()}
                      </p>
                    </div>
                    <DocStatusBadge report={report} className="flex-shrink-0" />
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-line-soft">
                    <div className="flex items-center gap-2">
                      <Badge tone={getReportTypeBadgeTone()} shape="rounded" dot>
                        {/* 유형 배지 = 서버가 내려주는 양식 이름(코드값·키는 화면에 새지 않는다). */}
                        {getReportTypeLabel(report.templateName)}
                      </Badge>
                      <span className="text-xs font-medium tabular-nums text-ink-400">
                        {formatDateToLocal(new Date(report.createdAt))}
                      </span>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <ReportDropdownMenu
                        status={report.status}
                        canDelete={canDeleteDoc(report).allowed}
                        deleteDisabledReason={canDeleteDoc(report).reason}
                        onView={() => navigateToDetail(report)}
                        onPrint={() => handlePrint(report.id, report.title)}
                        onShare={() => handleShareClick(report.id, report.title)}
                        onDelete={() => handleDeleteClick(report)}
                        hasPendingDeleteRequest={report.hasPendingDeleteRequest}
                        onCancelDeleteRequest={() => handleCancelDeleteRequestClick(report)}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card radius="lg" padding="none" className="overflow-hidden">
              {/* 표 카드 헤더 — 목업 `.table-card-h`. 목록이 지금 무엇을 보여주고 있는지를
                  표 위에서 먼저 알린다(정렬 셀렉트는 정렬 기능이 아직 없어 두지 않았다). */}
              <div className="flex items-center justify-between gap-3 border-b border-line-soft bg-gradient-to-b from-white to-surface-soft px-4 py-3.5">
                <p className="text-sm font-semibold text-ink-500">
                  전체 <span className="font-bold tabular-nums text-ink-800">{totalCount}</span>건의 문서 ·{' '}
                  <span className="font-bold tabular-nums text-ink-800">{reports.length}</span>건 표시 중
                </p>
              </div>
              {/* 가로 스크롤은 표에만 건다 — 카드에 걸면 헤더·페이저까지 함께 밀린다. */}
              <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full">
                <thead>
                  <tr>
                    <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[280px]`}>
                      문서
                    </th>
                    <th className={TABLE_HEADER_CELL_CLASS}>
                      유형
                    </th>
                    <th className={TABLE_HEADER_CELL_CLASS}>
                      현장
                    </th>
                    <th className={TABLE_HEADER_CELL_CLASS}>
                      작성자
                    </th>
                    <th className={TABLE_HEADER_CELL_CLASS}>
                      상태
                    </th>
                    <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[120px]`}>
                      생성일
                    </th>
                    <th className={`${TABLE_HEADER_CELL_CLASS} min-w-[120px]`}>
                      결재완료일
                    </th>
                    <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>
                      <span className="sr-only">작업</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-soft bg-white">
                  {reports.map((report) => {
                    // 유형 아이콘 — 모든 문서가 Composer 양식이라 기본 문서 아이콘 하나다(결재함과 같다 ·
                    // 유형별 픽토그램 맵은 없다). 타일 색은 `reportTypeDisplay`가 정본.
                    const TypeIcon = DocumentIcon;
                    const createdDistance = formatDayDistance(report.createdAt);
                    const approvedDistance = report.approvedAt ? formatDayDistance(report.approvedAt) : null;
                    return (
                    <tr
                      key={report.id}
                      onClick={() => navigateToDetail(report)}
                      className="cursor-pointer transition-colors hover:bg-surface-soft"
                    >
                      <td className="px-4 py-3.5 min-w-[280px]">
                        {/* 유형 아이콘 타일 — 목업 `.doc-ic`. 유형→아이콘·타일색은
                            `components/reports/reportTypeDisplay`가 단일 정본이다(결재함과 공유). */}
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-9 w-9 flex-none items-center justify-center rounded-control ${
                              getReportTypeTileClass(getReportTypeBadgeTone())
                            }`}
                          >
                            <TypeIcon className="h-[17px] w-[17px]" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-[15px] font-semibold tracking-tight text-ink-900">
                              {isTbmEducationDocument(report) && (() => { const ts = getTBMTagStyle(report.content?.tag as string); return ts ? <Badge tone={ts.tone} size="sm" className="mr-1">{ts.label}</Badge> : null; })()}
                              {getDocumentDisplayTitle(report)}
                            </div>
                            {isTbmEducationDocument(report) && (
                              <div className="text-xs font-medium text-ink-400 mt-1 flex items-center gap-2 whitespace-nowrap">
                                <span>교육담당: {report.distributionTokens?.[0]?.contactName || getEvidenceDisplayName(report.authorSignerName ?? report.authorSnapshotName ?? null, toListAuthorActor(report.author), '-')}</span>
                                {report.attendeeCount != null && report.attendeeCount > 0 && (
                                  <span className="flex items-center">
                                    <UsersIcon className="h-3.5 w-3.5 mr-0.5" />
                                    서명 {report.attendeeCount}명
                                  </span>
                                )}
                                {(() => {
                                  const dur = getTBMDuration(report);
                                  return dur ? (
                                    <span className="flex items-center text-ink-400">
                                      <ClockIcon className="h-3.5 w-3.5 mr-0.5" />
                                      {report.completedAt ? dur : `진행 ${dur}`}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <Badge tone={getReportTypeBadgeTone()} shape="rounded" dot>
                          {/* 유형 배지 = 서버가 내려주는 양식 이름(코드값·키는 화면에 새지 않는다). */}
                          {getReportTypeLabel(report.templateName)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm font-medium text-ink-600">
                        {/* 현장 핀 — 목업 `.site svg`. 같은 열의 값이 전부 지명이라 아이콘이
                            "이 칸은 장소"라는 신호가 된다(결재함 메타 행과 같은 규격). */}
                        {report.site?.name ? (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPinIcon className="h-3.5 w-3.5 flex-none text-ink-400" />
                            {report.site.name}
                          </span>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm font-medium text-ink-600">
                        {getEvidenceDisplayName(report.authorSignerName ?? report.authorSnapshotName ?? null, toListAuthorActor(report.author), '-')}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <DocStatusBadge report={report} />
                      </td>
                      {/* 날짜 칸 2줄 — 목업 `.date`(절대 날짜 + 아래 상대 표기).
                          날짜만 있으면 "언제 일인지" 매번 계산해야 읽힌다. */}
                      <td className="px-4 py-3.5 whitespace-nowrap min-w-[120px]">
                        <div className="text-sm font-semibold tabular-nums text-ink-800">
                          {formatDateToLocal(new Date(report.createdAt))}
                        </div>
                        {createdDistance && (
                          <div className="mt-0.5 text-xs font-medium text-ink-400">{createdDistance}</div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap min-w-[120px]">
                        {report.approvedAt ? (
                          <>
                            <div className="text-sm font-semibold tabular-nums text-ink-800">
                              {formatDateToLocal(new Date(report.approvedAt))}
                            </div>
                            {approvedDistance && (
                              <div className="mt-0.5 text-xs font-medium text-ink-400">{approvedDistance}</div>
                            )}
                          </>
                        ) : (
                          // 목업 `.date .empty` — 빈 값은 본문보다 옅게 둬서 "아직 없음"으로 읽히게.
                          <span className="text-sm font-medium text-ink-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right">
                        <ReportDropdownMenu
                          status={report.status}
                          canDelete={canDeleteDoc(report).allowed}
                          deleteDisabledReason={canDeleteDoc(report).reason}
                          onView={() => navigateToDetail(report)}
                          onPrint={() => handlePrint(report.id, report.title)}
                          onShare={() => handleShareClick(report.id, report.title)}
                          onDelete={() => handleDeleteClick(report)}
                          hasPendingDeleteRequest={report.hasPendingDeleteRequest}
                          onCancelDeleteRequest={() => handleCancelDeleteRequestClick(report)}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {/* 페이지네이션 — 목업 `.pager`는 표 카드의 **바닥**이다. 별도 카드로 띄우면
                  표와 페이저 사이에 여백이 생겨 어느 표의 페이저인지가 흐려진다. */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 border-t border-line-soft bg-surface-soft px-4 py-3.5">
                  <p className="text-sm font-medium text-ink-500">
                    총 <span className="font-bold tabular-nums text-ink-800">{totalCount}</span>건 중{' '}
                    <span className="font-bold tabular-nums text-ink-800">
                      {(page - 1) * limit + 1}-{Math.min(page * limit, totalCount)}
                    </span>{' '}
                    표시
                  </p>
                  <nav className="flex items-center gap-1" aria-label="페이지">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className={`${PAGER_BUTTON_CLASS} px-2`}
                      aria-label="이전 페이지"
                    >
                      <ChevronLeftIcon className="h-3.5 w-3.5" />
                    </button>
                    {buildPagerRange(page, totalPages).map((slot, i) =>
                      slot === 'gap' ? (
                        // 생략은 버튼이 아니다 — 누를 게 없는데 버튼이면 탭 이동만 늘어난다.
                        <span
                          key={`gap-${i}`}
                          aria-hidden="true"
                          className="px-1 text-sm font-semibold text-ink-400"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setPage(slot)}
                          aria-current={slot === page ? 'page' : undefined}
                          aria-label={`${slot}페이지`}
                          // 현재 페이지 표시는 결재함 페이저(`pages/approvals/index.tsx`)와 같은
                          // 문자열이다 — 같은 목업 규칙(`.pager .nav button.cur`)이라 갈리면 안 된다.
                          className={`h-8 min-w-[32px] rounded-card-sm border px-2 text-sm font-semibold tabular-nums transition-colors ${
                            slot === page
                              ? 'border-brand-600 bg-brand-600 text-white'
                              : 'border-line bg-white text-ink-600 hover:bg-surface-hover hover:text-ink-900'
                          }`}
                        >
                          {slot}
                        </button>
                      ),
                    )}
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className={`${PAGER_BUTTON_CLASS} px-2`}
                      aria-label="다음 페이지"
                    >
                      <ChevronRightIcon className="h-3.5 w-3.5" />
                    </button>
                  </nav>
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* 모바일 페이지네이션 — 데스크톱은 표 카드 바닥(목업 `.pager`)으로 들어갔고,
          모바일은 담을 표가 없어(카드 목록) 이 자리에 남는다. 좁은 화면에서는 번호를 나열할
          가로 폭이 없으므로 이전/다음 + 현재 위치만 둔다. */}
      {totalPages > 1 && isMobile && (
        <Card padding="none" className="flex items-center justify-between px-4 py-3">
          <div className="text-sm font-medium tabular-nums text-ink-500">
            {`${(page - 1) * limit + 1}-${Math.min(page * limit, totalCount)} / ${totalCount}`}
          </div>
          <div className="flex items-center gap-1.5">
            {/* 목업 .pager 톤 = secondary/sm. 글자만 기존 text-sm 유지(sm 기본은 text-xs). */}
            <Button
              variant="secondary"
              size="sm"
              className="text-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              이전
            </Button>
            <span className="px-2 text-sm font-semibold tabular-nums text-ink-700">
              {page} / {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="text-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              다음
            </Button>
          </div>
        </Card>
      )}

      {/* 삭제 확인 다이얼로그 */}
      <ReportDeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setReportToDelete(null);
        }}
        onDelete={handleDeleteConfirm}
        report={reportToDelete ? {
          title: reportToDelete.title,
          status: reportToDelete.status,
          approvalSignatures: reportToDelete.approvalSignatures,
          educationSessionCount: reportToDelete.educationSessionCount,
          hasApprovalInProgress: reportToDelete.hasApprovalInProgress,
          postSubmitFlowKey: reportToDelete.postSubmitFlowKey,
          authorName: reportToDelete.authorName,
          siteName: reportToDelete.siteName,
        } : null}
        isLoading={isDeleting}
        requiresApproval={reportToDelete?.requiresApproval}
        finalApproverName={reportToDelete?.finalApproverName}
        onRequestDelete={handleRequestDelete}
      />

      {/* 삭제 요청 취소 확인 모달 — 아이콘+제목이 본문 머리라 base 헤더를 쓰지 않는다(종전에도 ✕ 없음).
          요청 취소 중에는 닫는 길을 전부 막는다 — 종전엔 오버레이 클릭으로 도중에 닫혔다. */}
      <Modal
        open={showCancelDeleteRequestDialog && !!cancelDeleteTarget}
        onClose={closeCancelDeleteRequestDialog}
        ariaLabel="삭제 요청 취소"
        size="md"
        showCloseButton={false}
        closeOnOverlay={!isCancellingDeleteRequest}
        closeOnEsc={!isCancellingDeleteRequest}
        closeLocked={isCancellingDeleteRequest}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeCancelDeleteRequestDialog}
              disabled={isCancellingDeleteRequest}
            >
              닫기
            </Button>
            {/* 요청 취소는 되돌리는 액션 — 파괴적이지 않아 warning. */}
            <Button
              variant="warning"
              onClick={handleCancelDeleteRequestConfirm}
              disabled={isCancellingDeleteRequest}
            >
              {isCancellingDeleteRequest ? '취소 중...' : '요청 취소'}
            </Button>
          </>
        }
      >
        {cancelDeleteTarget && (
              <div className="flex flex-col sm:flex-row sm:items-start">
                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-amber-100 mx-auto sm:mx-0">
                  <ArrowUturnLeftIcon className="h-5 w-5 text-amber-600" />
                </div>
                <div className="mt-3 sm:mt-0 sm:ml-4 flex-1 text-center sm:text-left min-w-0">
                  <h3 className="text-lg font-medium text-ink-900">삭제 요청 취소</h3>
                  <div className="mt-3 p-3 bg-surface-soft rounded-card">
                    <p className="text-sm font-medium text-ink-900 truncate">{cancelDeleteTarget.title}</p>
                  </div>
                  <p className="mt-3 text-sm text-ink-500">
                    이 보고서의 삭제 승인 요청을 취소하시겠습니까?
                  </p>
                </div>
              </div>
        )}
      </Modal>

      {/* PDF 상태 모달 — 아이콘+제목 가운데 정렬 안내형이라 base 헤더를 쓰지 않는다(종전에도 ✕ 없음).
          버튼 정렬은 종전 가운데에서 base 푸터의 오른쪽 정렬로 바뀐다. */}
      <Modal
        open={showPdfModal && !!pdfModalState}
        onClose={() => setShowPdfModal(false)}
        ariaLabel="PDF 상태"
        size="md"
        showCloseButton={false}
        footer={
          pdfModalState && (
            <>
              <Button variant="secondary" onClick={() => setShowPdfModal(false)}>
                닫기
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowPdfModal(false);
                  navigate(`/reports/${pdfModalState.reportId}`, { state: listEntryState });
                }}
              >
                상세 보기
              </Button>
            </>
          )
        }
      >
        {pdfModalState && (
              <div className="text-center">
                {pdfModalState.status === 'GENERATING' || pdfModalState.status === 'PENDING' ? (
                  <>
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-brand-100 mb-4">
                      <LoadingIcon className="h-6 w-6 text-brand-600 animate-spin" />
                    </div>
                    <h3 className="text-lg font-medium text-ink-900">PDF 생성 중</h3>
                    <p className="mt-2 text-sm text-ink-500">
                      PDF 파일을 생성하고 있습니다.<br />
                      잠시 후 다시 시도해주세요.
                    </p>
                  </>
                ) : pdfModalState.status === 'FAILED' ? (
                  <>
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 mb-4">
                      <ExclamationIcon className="h-6 w-6 text-rose-600" />
                    </div>
                    <h3 className="text-lg font-medium text-ink-900">PDF 생성 실패</h3>
                    <p className="mt-2 text-sm text-ink-500">
                      PDF 생성에 실패했습니다.<br />
                      상세 페이지에서 재생성해주세요.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4">
                      <DocumentIcon className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="text-lg font-medium text-ink-900">PDF 준비 중</h3>
                    <p className="mt-2 text-sm text-ink-500">
                      PDF 파일이 아직 생성되지 않았습니다.<br />
                      상세 페이지에서 PDF를 생성해주세요.
                    </p>
                  </>
                )}
              </div>
        )}
      </Modal>

      {/* 공유 모달 */}
      {shareReportId && companyId && (
        <ShareReportModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setShareReportId(null);
            setShareReportTitle('');
          }}
          reportId={shareReportId}
          reportTitle={shareReportTitle}
          companyId={companyId}
        />
      )}

      {/* FAB Speed Dial 지원 모달 */}
      <SiteFormModal />
      <CreateInspectionModal />

    </div>
  );
}

// Icons
// 문서·시계·사람 아이콘은 공용 세트(`components/icons`)로 옮겼다 — 같은 그림을 페이지마다
// 다시 그리면 1-A에서 통일한 획 굵기·크기 규격이 여기서만 어긋난다.
// 스피너는 공용 세트에 대응물이 없어(아이콘이 아니라 로딩 표시) 여기 남는다.

function LoadingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
